import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FaArrowLeft, FaCheckCircle, FaClipboardCheck, FaCopy, FaXRay } from "react-icons/fa";
import Header from "../../../components/IntranetHeader";
import Footer from "../../../components/Footer";
import { supabase } from "../../../supabaseClient";

const RESULT_FLAGS = ["Normal", "Abnormal", "Critical"];
const RUHT_BASE_URL = "https://ruht.cloud";

function formatDateTime(value) {
  if (!value) return "Not recorded";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

async function authHeaders(json = false) {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  return {
    ...(json ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export default function RadiologyStudy({ session, profile }) {
  const { studyId } = useParams();
  const [study, setStudy] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [draft, setDraft] = useState({
    findings: "",
    impression: "",
    resultFlag: "Abnormal",
    patientVisible: false,
  });

  const studyLink = useMemo(
    () => `${RUHT_BASE_URL}/intranet/ecamis/imaging/${encodeURIComponent(studyId || "")}`,
    [studyId],
  );

  const load = useCallback(async () => {
    if (!studyId) return;
    setLoading(true);
    setError("");

    try {
      const headers = await authHeaders();
      const metadataResponse = await fetch(`/api/radiology?action=get&id=${encodeURIComponent(studyId)}`, { headers });
      const metadata = await metadataResponse.json().catch(() => ({}));
      if (!metadataResponse.ok) throw new Error(metadata.error || "The imaging study could not be loaded.");

      const nextStudy = metadata.study;
      setStudy(nextStudy);
      setDraft({
        findings: nextStudy?.findings || "",
        impression: nextStudy?.impression || "",
        resultFlag: nextStudy?.diagnostic?.result_flag || "Abnormal",
        patientVisible: nextStudy?.diagnostic?.patient_visible === true,
      });

      if (nextStudy?.generation_status === "Ready") {
        const imageResponse = await fetch(`/api/radiology?action=image&id=${encodeURIComponent(studyId)}`, { headers });
        if (!imageResponse.ok) throw new Error("The generated imaging file is unavailable.");
        const blob = await imageResponse.blob();
        const objectUrl = URL.createObjectURL(blob);
        setImageUrl((previous) => {
          if (previous) URL.revokeObjectURL(previous);
          return objectUrl;
        });
      }
    } catch (loadError) {
      setError(loadError.message || "The imaging study could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [studyId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => () => {
    if (imageUrl) URL.revokeObjectURL(imageUrl);
  }, [imageUrl]);

  async function submitReport(finalise) {
    if (!studyId) return;
    setSaving(true);
    setError("");
    setMessage("");

    try {
      const response = await fetch("/api/radiology", {
        method: "POST",
        headers: await authHeaders(true),
        body: JSON.stringify({
          action: "saveReport",
          studyId,
          findings: draft.findings,
          impression: draft.impression,
          resultFlag: draft.resultFlag,
          patientVisible: draft.patientVisible,
          finalise,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || "The report could not be saved.");
      setStudy(payload.study);
      setMessage(finalise ? "Radiology report finalised and returned to the clinical worklist." : "Radiology report draft saved.");
    } catch (saveError) {
      setError(saveError.message || "The report could not be saved.");
    } finally {
      setSaving(false);
    }
  }

  async function markReviewed() {
    if (!studyId) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const response = await fetch("/api/radiology", {
        method: "POST",
        headers: await authHeaders(true),
        body: JSON.stringify({ action: "markReviewed", studyId }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || "The report could not be marked as reviewed.");
      setStudy(payload.study);
      setMessage("Imaging report marked as reviewed.");
    } catch (reviewError) {
      setError(reviewError.message || "The report could not be marked as reviewed.");
    } finally {
      setSaving(false);
    }
  }

  async function copyStudyLink() {
    try {
      await navigator.clipboard.writeText(studyLink);
      setMessage("RUHT Cloud study link copied.");
    } catch {
      setError("The study link could not be copied automatically.");
    }
  }

  const reportLocked = study?.report_status === "Reviewed";

  return (
    <div className="flex flex-col min-h-screen">
      <Header session={session} profile={profile} />
      <main className="nhsuk-width-container pguh-main ruht-radiology-page">
        <div className="ruht-radiology-page__topbar">
          <Link className="nhsuk-back-link" to="/intranet/ecamis/clinical-worklist">
            <FaArrowLeft aria-hidden="true" /> Back to clinical worklist
          </Link>
          <span className={`ruht-radiology-status ruht-radiology-status--${String(study?.report_status || "loading").toLowerCase().replace(/\s+/g, "-")}`}>
            {study?.report_status || "Loading"}
          </span>
        </div>

        <div className="pguh-section-heading">
          <div>
            <span className="pguh-eyebrow">eCAMIS Radiology</span>
            <h1 className="nhsuk-heading-l"><FaXRay aria-hidden="true" /> Imaging study</h1>
            <p className="nhsuk-body-m">AI-selected Roblox roleplay cases rendered against the exact R6 body-part template for clinician interpretation and reporting.</p>
          </div>
        </div>

        {message && <div className="pguh-timesheet-message pguh-timesheet-message--success">{message}</div>}
        {error && <div className="pguh-timesheet-message pguh-timesheet-message--error">{error}</div>}

        {loading ? (
          <div className="pguh-empty-state"><h2>Loading imaging study…</h2></div>
        ) : study ? (
          <>
            <section className="ruht-radiology-summary">
              <div><span>Patient</span><strong>{study.patient?.full_name || "Patient"}</strong><small>{study.patient?.roblox_username ? `@${study.patient.roblox_username}` : ""}</small></div>
              <div><span>Study</span><strong>{study.modality} · {study.laterality} {study.body_part}</strong><small>{study.projection}</small></div>
              <div><span>Priority</span><strong>{study.diagnostic?.priority || "Routine"}</strong><small>Requested {formatDateTime(study.diagnostic?.requested_at || study.created_at)}</small></div>
              <div><span>Generation</span><strong>{study.generation_status}</strong><small>{study.ai_model ? `AI model: ${study.ai_model}` : ""}</small></div>
            </section>

            <div className="ruht-radiology-linkbar">
              <div><strong>RUHT Cloud study link</strong><a href={studyLink}>{studyLink}</a></div>
              <button type="button" onClick={copyStudyLink}><FaCopy aria-hidden="true" /> Copy link</button>
            </div>

            <div className="ruht-radiology-layout">
              <section className="ruht-radiology-viewer">
                <div className="ruht-radiology-viewer__header">
                  <div><strong>{study.modality}</strong><span>{study.laterality} {study.body_part} · {study.projection}</span></div>
                  <span>FICTIONAL ROLEPLAY STUDY</span>
                </div>
                <div className="ruht-radiology-viewer__stage">
                  {imageUrl ? <img src={imageUrl} alt={`${study.modality} of ${study.laterality} ${study.body_part}`} /> : <p>Generated image unavailable.</p>}
                </div>
                <div className="ruht-radiology-viewer__notice">
                  This fictional study uses the exact R6 body-part geometry from the supplied reference. The hidden case is not shown here; use the image itself to write the report.
                </div>
              </section>

              <section className="ruht-radiology-report">
                <div className="ruht-radiology-report__heading">
                  <div><span className="pguh-eyebrow">Clinician report</span><h2 className="nhsuk-heading-m">Radiology findings</h2></div>
                  {study.report_status === "Reviewed" && <FaCheckCircle aria-hidden="true" />}
                </div>

                {study.diagnostic?.clinical_details && (
                  <div className="nhsuk-inset-text"><strong>Clinical details:</strong> {study.diagnostic.clinical_details}</div>
                )}

                <label className="nhsuk-label" htmlFor="radiology-findings">Findings</label>
                <textarea
                  id="radiology-findings"
                  className="nhsuk-textarea"
                  rows={8}
                  value={draft.findings}
                  disabled={reportLocked}
                  onChange={(event) => setDraft((current) => ({ ...current, findings: event.target.value }))}
                  placeholder="Describe alignment, osseous findings, soft tissues and any other relevant features seen on the study."
                />

                <label className="nhsuk-label" htmlFor="radiology-impression">Impression</label>
                <textarea
                  id="radiology-impression"
                  className="nhsuk-textarea"
                  rows={4}
                  value={draft.impression}
                  disabled={reportLocked}
                  onChange={(event) => setDraft((current) => ({ ...current, impression: event.target.value }))}
                  placeholder="Concise diagnostic impression / conclusion."
                />

                <label className="nhsuk-label" htmlFor="radiology-result-flag">Result flag</label>
                <select
                  id="radiology-result-flag"
                  className="nhsuk-select"
                  value={draft.resultFlag}
                  disabled={reportLocked}
                  onChange={(event) => setDraft((current) => ({ ...current, resultFlag: event.target.value }))}
                >
                  {RESULT_FLAGS.map((flag) => <option key={flag}>{flag}</option>)}
                </select>

                <label className="pguh-inline-check ruht-radiology-share">
                  <input
                    type="checkbox"
                    checked={draft.patientVisible}
                    disabled={reportLocked}
                    onChange={(event) => setDraft((current) => ({ ...current, patientVisible: event.target.checked }))}
                  />
                  Share the written result in the patient portal after finalising
                </label>

                <div className="ruht-radiology-report__actions">
                  {!reportLocked && <button className="nhsuk-button nhsuk-button--secondary" type="button" disabled={saving} onClick={() => submitReport(false)}>Save draft</button>}
                  {!reportLocked && <button className="nhsuk-button" type="button" disabled={saving} onClick={() => submitReport(true)}>{saving ? "Saving…" : "Finalise report"}</button>}
                  {study.report_status === "Finalised" && (
                    <button className="nhsuk-button" type="button" disabled={saving} onClick={markReviewed}><FaClipboardCheck aria-hidden="true" /> Mark reviewed</button>
                  )}
                </div>

                <dl className="ruht-radiology-audit">
                  <div><dt>Generated</dt><dd>{formatDateTime(study.generated_at)}</dd></div>
                  <div><dt>Report status</dt><dd>{study.report_status}</dd></div>
                  <div><dt>Reported</dt><dd>{formatDateTime(study.reported_at)}</dd></div>
                  <div><dt>Reviewed</dt><dd>{formatDateTime(study.reviewed_at)}</dd></div>
                </dl>
              </section>
            </div>
          </>
        ) : null}
      </main>
      <Footer />
    </div>
  );
}
